import React, { useState, useCallback } from 'react';
import { View, FlatList, TouchableOpacity, RefreshControl, ActivityIndicator } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useFocusEffect } from '@react-navigation/native';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Icon from '../../../../components/IcomoonIcon';
import IconButton from '../../../../components/IconButton';
import api from '../../../../utils/api';
import { useSelector } from 'react-redux';
import useActiveResidentCommunity from '../../../../hooks/useActiveResidentCommunity';

import NewTicketModal from './NewTicketModal';


const STATUS_STYLES: any = {
  nuevo: { bg: 'bg-red-50', text: 'text-red-600', label: 'Nuevo' },
  en_seguimiento: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'En seguimiento' },
  atendida: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Atendida' },
};


export default function TicketsListScreen({ navigation }: any) {
  const { communityId } = useActiveResidentCommunity();
  const [showNew, setShowNew] = useState(false);
  const auth = useSelector((s: any) => s.auth);

  const ticketsQuery = useQuery<any>({
    queryKey: ['resident.tickets', communityId],
    queryFn: () => api.get(`/communities/${communityId}/tickets`).then(r => r.data),
    enabled: !!communityId,
  });

  const areasQuery = useQuery<any>({
    queryKey: ['resident.ticket-areas', communityId],
    queryFn: () => api.get(`/communities/${communityId}/ticket-areas`).then(r => r.data),
    enabled: !!communityId,
  });

  // Misma caché que el dashboard del residente: de ahí sale si puede elegir categoría.
  const dashboardQuery = useQuery<any>({
    queryKey: ['resident.community.dashboard', communityId, auth.user?.email],
    queryFn: () => api.get(`/communities/${communityId}/resident/dashboard`).then(r => r.data),
    enabled: !!communityId,
  });
  const canChooseCategory = dashboardQuery.data?.settings?.can_choose_ticket_category === true;

  const categoriesQuery = useQuery<any>({
    queryKey: ['resident.ticket-categories', communityId],
    queryFn: () => api.get(`/communities/${communityId}/ticket-categories`).then(r => r.data),
    enabled: !!communityId && canChooseCategory,
  });

  useFocusEffect(useCallback(() => {
    ticketsQuery.refetch();
  }, [communityId]));

  const tickets: any[] = ticketsQuery.data?.data ?? [];
  const areas: any[] = areasQuery.data ?? [];

  return (
    <View style={tw`flex-1 bg-background`}>
      <View style={tw`bg-secondary pt-14 pb-5 px-5 flex-row items-center`}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={tw`mr-3`}>
          <Icon name="chevron-left" color="white" size={20} />
        </TouchableOpacity>
        <Text style={tw`text-white text-xl font-500 flex-1`}>Mis reportes</Text>
      </View>

      {ticketsQuery.isLoading && tickets.length === 0 ? (
        <View style={tw`flex-1 items-center justify-center`}>
          <ActivityIndicator color="#6366f1" />
        </View>
      ) : (
        <FlatList
          data={tickets}
          keyExtractor={(t) => t.uuid}
          contentContainerStyle={tw`px-4 pt-4 pb-24`}
          refreshControl={
            <RefreshControl refreshing={ticketsQuery.isFetching} onRefresh={() => ticketsQuery.refetch()} />
          }
          ListEmptyComponent={
            <View style={tw`pt-20 items-center px-8`}>
              <Text style={tw`text-gray-400 text-center mb-4`}>
                Aún no has creado ningún reporte.
              </Text>
              <IconButton title="Crear reporte" onPress={() => setShowNew(true)} />
            </View>
          }
          renderItem={({ item }) => (
            <TicketRow
              ticket={item}
              onPress={() => navigation.navigate('Resident.Tickets.Detail', { ticketUuid: item.uuid })}
            />
          )}
        />
      )}

      <IconButton
        title={"Nuevo reporte"}
        color='secondary'
        textColor='white'
        style={tw`absolute bottom-4 self-center`}
        onPress={() => setShowNew(true)}
      />

      <NewTicketModal
        visible={showNew}
        communityId={communityId}
        areas={areas}
        categories={canChooseCategory ? (categoriesQuery.data ?? []) : []}
        onRequestClose={() => setShowNew(false)}
        onCreated={() => {
          setShowNew(false);
          ticketsQuery.refetch();
        }}
      />
    </View>
  );
}

function TicketRow({ ticket, onPress }: any) {
  const status = STATUS_STYLES[ticket.status] ?? STATUS_STYLES.nuevo;
  return (
    <TouchableOpacity onPress={onPress} style={tw`bg-white rounded-2xl border border-gray-200 p-4 mb-3`}>
      <View style={tw`flex-row items-center mb-1`}>
        <Text style={tw`text-xs text-gray-400 flex-1`}>{ticket.folio}</Text>
        {
          ticket.has_new_messages &&
            <View style={tw`w-2.5 h-2.5 rounded-full bg-red-500 mr-2`}></View>
        }
        <Text style={tw.style(`text-xs font-500 px-2 py-0.5 rounded-full`, status.bg, status.text)}>
          {status.label}
        </Text>
      </View>
      <Text style={tw`text-sm text-gray-700`} numberOfLines={2}>{ticket.description}</Text>
      <View style={tw`flex-row items-center justify-between mt-2`}>
        <Text style={tw`text-xs text-gray-500 flex-1 mr-2`} numberOfLines={1}>
          {ticket.category?.name ? `${ticket.category.name} · ` : ''}{ticket.area?.name ?? 'Por asignar'}
        </Text>
        <Text style={tw`text-xs text-gray-400`}>{dayjs(ticket.created_at).format('D MMM · H:mm')}</Text>
      </View>
    </TouchableOpacity>
  );
}
